<template>
  <div class="discount-tickets">
    <SDialog v-model:open="dialogVisible">
      <SDialogContent
        class="discount-tickets !w-[70%] !max-w-3xl"
        @escape-key-down.prevent
        @pointer-down-outside.prevent
        @open-auto-focus.prevent
      >
        <SDialogHeader>
          <SDialogTitle>{{ $t("discount.select-ticket") }}</SDialogTitle>
        </SDialogHeader>
        <div class="min-w-0">
          <SInputGroup class="mb-4">
            <SInputGroupInput
              :placeholder="$t('field.search')"
              :model-value="ticketsQuery"
              @update:model-value="(v) => (ticketsQuery = String(v))"
            />
            <SInputGroupAddon align="inline-end">
              <SearchIcon />
            </SInputGroupAddon>
          </SInputGroup>
          <div
            v-for="price in searchTickets"
            :key="price.priceId"
            :class="[
              'flex cursor-pointer items-center justify-between gap-4 border-b border-border px-3 py-3.5 transition-colors',
              isPriceSelected(price.priceId)
                ? 'bg-primary/5'
                : 'hover:bg-muted/50',
            ]"
            @click="
              !isPriceSelected(price.priceId)
                ? selectDiscountTickets(price.priceId)
                : deSelectDiscountTickets(price.priceId)
            "
          >
            <p class="min-w-0 truncate">
              <span class="font-semibold">{{ price.categoryName }}</span>
              <span class="text-muted-foreground">
                · {{ price.priceType }} · {{ price.priceName }}
              </span>
            </p>
            <div class="flex shrink-0 items-center gap-4">
              <span class="font-semibold tabular-nums">
                {{ currency + " " + price.priceValue / 100 }}
              </span>
              <CheckIcon
                class="size-5 text-primary"
                style="--s-icon-size: 20px"
                :style="{
                  visibility: isPriceSelected(price.priceId)
                    ? 'visible'
                    : 'hidden',
                }"
              />
            </div>
          </div>
        </div>
        <SDialogFooter>
          <div class="d-flex flex-row fl-s-b w-full">
            <SButton
              type="button"
              variant="outline"
              size="lg"
              @click="cancelSelection()"
            >
              {{ $t("button.cancel") }}
            </SButton>

            <el-pagination
              background
              small
              layout="prev, pager, next"
              :total="totalPssListLength"
              :current-page="currentPage"
              @current-change="changeCurrentPage"
              :page-size="pageSize"
            >
            </el-pagination>

            <SButton type="button" size="lg" @click="confirmSelection()">
              {{ $t("button.confirm") }}
            </SButton>
          </div>
        </SDialogFooter>
      </SDialogContent>
    </SDialog>
  </div>
</template>

<script lang="ts">
import { ModalComponents, ModalStatus } from "@/constants/modal";
import BookingModule from "@/store/modules/booking";
import ModalModule from "@/store/modules/modal";
import { dialogVisibleForStatus } from "@/utils/vue-helpers/modal";
import { computed, defineComponent, ref } from "vue";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogFooter,
  SDialogHeader,
  SDialogTitle,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";
import { CheckIcon, SearchIcon } from "@smeetz/ui-icons";

interface IDiscountPrice {
  priceId: number;
  categoryName: string;
  priceName: string;
  priceValue: number;
  priceType: string;
}

export default defineComponent({
  name: ModalComponents.DiscountSelectTickets,
  components: {
    SButton,
    SDialog,
    SDialogContent,
    SDialogFooter,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
    CheckIcon,
    SearchIcon,
    SDialogHeader,
    SDialogTitle,
  },
  setup() {
    const ticketsQuery = ref("");
    let pageSize = ref(5);
    let currentPage = ref(1);
    const changeCurrentPage = (val: number) => {
      currentPage.value = val;
    };

    const dialogVisible = dialogVisibleForStatus(
      ModalStatus.DiscountSelectTickets
    );

    const totalPssListLength = computed(() => {
      return BookingModule.selectedBooking?.pssList.length;
    });

    // A list of prices of the booking (no duplicates)
    const pricesList = computed(() => {
      const nonDuplicatedPricesIds = [
        ...new Set(
          BookingModule.selectedBooking?.pssList.map((pss) => pss.priceId)
        ),
      ];

      let prices: IDiscountPrice[] = [];

      nonDuplicatedPricesIds.forEach((priceId) => {
        let pp = BookingModule.selectedBooking?.pssList.find(
          (pss) => pss.priceId === priceId
        );

        if (pp)
          prices.push({
            priceId: pp.priceId,
            categoryName: pp.categoryName,
            priceName: pp.priceName,
            priceValue: pp.priceValue,
            priceType: pp.pssType,
          });
      });

      return prices;
    });

    let selectedPrices = ref([] as number[]);

    fillDiscountPrices(selectedPrices.value);

    function fillDiscountPrices(pricesIds: number[]) {
      const discountPrices =
        BookingModule.selectedBookingDiscountTrack?.pssDiscounts.map(
          (pss) => pss.priceId
        );
      if (!discountPrices || !discountPrices.length) {
        return [];
      }
      for (const priceId of discountPrices) {
        if (pricesIds.findIndex((item) => item === Number(priceId)) > -1) {
          continue;
        }
        pricesIds.push(Number(priceId));
      }
    }

    const searchTickets = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      return pricesList.value
        .slice(start, end)
        .filter(
          (price: any) =>
            price.categoryName
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            price.priceName
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            price.priceType
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase()) ||
            String(price.priceValue)
              .toLowerCase()
              .includes(ticketsQuery.value.toLowerCase())
        );
    });

    const currency = computed(() => {
      return BookingModule.selectedBooking?.currency;
    });

    function selectDiscountTickets(priceId: number) {
      selectedPrices.value.push(priceId);
    }

    function deSelectDiscountTickets(priceId: number) {
      const index = selectedPrices.value.findIndex((cat) => cat === priceId);
      if (index > -1) {
        selectedPrices.value.splice(index, 1);
      }
    }

    function confirmSelection() {
      // going to make discount appliable on some tickets
      BookingModule.updateDiscountCoverage({
        BookingUUID: BookingModule.selectedBookingUUID,
        isOnAllOrder: false,
      });
      BookingModule.updateDiscountPrices({
        BookingUUID: BookingModule.selectedBookingUUID,
        pricesIds: selectedPrices.value,
      });
      ModalModule.setStatus(ModalStatus.Hidden);
    }

    function cancelSelection() {
      // Clear the selected tickets and close it
      selectedPrices.value = [];
      ModalModule.setStatus(ModalStatus.Hidden);
    }

    function isPriceSelected(priceId: number) {
      return selectedPrices.value.some((prId) => prId === priceId);
    }

    return {
      dialogVisible,
      pricesList,
      currency,
      isPriceSelected,
      selectDiscountTickets,
      deSelectDiscountTickets,
      confirmSelection,
      cancelSelection,
      totalPssListLength,
      ticketsQuery,
      searchTickets,
      pageSize,
      currentPage,
      changeCurrentPage,
    };
  },
});
</script>

<style lang="scss" scoped></style>
